import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { newIdempotencyKey } from "./api.js";
const isAbort = (e) => e?.name === "AbortError";
/**
 * Loads data when `deps` change. A request that is no longer wanted (the user moved on) is cancelled and its answer
 * is ignored. Previous data stays on screen while a reload runs, so the page does not flash.
 */
export function useResource(load, deps) {
    const [state, setState] = useState({ data: undefined, error: undefined, loading: true });
    const [tick, setTick] = useState(0);
    const waiters = useRef([]);
    const latest = useRef(load);
    useLayoutEffect(() => { latest.current = load; });
    useEffect(() => {
        const ac = new AbortController();
        setState((s) => ({ ...s, loading: true, error: undefined }));
        // Whoever asked for a reload is released only when a load that is still wanted has settled.
        const release = () => { const w = waiters.current; waiters.current = []; w.forEach((f) => f()); };
        latest.current(ac.signal).then((data) => { if (!ac.signal.aborted) {
            setState({ data, error: undefined, loading: false });
            release();
        } }, (error) => { if (!ac.signal.aborted && !isAbort(error)) {
            setState((s) => ({ data: s.data, error: error, loading: false }));
            release();
        } });
        return () => ac.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, tick]);
    const reload = useCallback(() => new Promise((resolve) => { waiters.current.push(resolve); setTick((t) => t + 1); }), []);
    return { ...state, reload };
}
/** Runs a write. Failures are kept for display rather than thrown into the render; success returns the result. */
export function useAction(fn) {
    const [running, setRunning] = useState(false);
    const [error, setError] = useState(null);
    const alive = useRef(true);
    const latest = useRef(fn);
    useLayoutEffect(() => { latest.current = fn; });
    useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
    const run = useCallback(async (...args) => {
        setRunning(true);
        setError(null);
        try {
            return await latest.current(...args);
        }
        catch (e) {
            if (alive.current && !isAbort(e))
                setError(e);
            return undefined;
        }
        finally {
            if (alive.current)
                setRunning(false);
        }
    }, []);
    const reset = useCallback(() => setError(null), []);
    return { run, running, error, reset };
}
/**
 * One idempotency key per logical action. Keep it while the action fails or is retried (a retry of a request that
 * actually succeeded is then replayed, not repeated), and call `renew` after success so the next action is new.
 */
export function useIdempotencyKey() {
    const ref = useRef(null);
    return {
        key: () => (ref.current ??= newIdempotencyKey()),
        renew: () => { ref.current = null; },
    };
}
